import { Button } from 'reactstrap';
import { TagItem } from '@features/survey/question/tags/TagItem';
import { InitiativeUniversalTracker } from '@g17eco/types/initiativeUniversalTracker';
import { Checkbox, CheckboxState } from '@g17eco/atoms/checkbox';
import { InitiativeQuestionManagementResponse } from '@api/admin-dashboard';
import { UniversalTrackerBlueprintMin } from '@g17eco/types/universalTracker';
import { QUESTION } from '@constants/terminology';
import {
  QuestionConfigurationDropdown,
  ConfigurationType,
} from '@features/question-configuration';
import { ColumnDef, Table } from '@g17eco/molecules/table';
import { useContext } from 'react';
import { QuestionManagementContext } from './QuestionManagementContainer';
import { BulkActionUtr } from '@g17eco/types/survey-question-list';
import { getDisplayedInputOverrides, getDisplayedOverrides } from './displayUtils';

export enum QuestionTableColumn {
  Tags = 'Tags',
  Decimals = 'Decimals',
  NumberScale = 'Number scale',
  Unit = 'Unit type',
  Overrides = 'Overrides',
  MultiSelectAction = 'Multiple select action',
  SingleAction = 'Single action',
}

interface QuestionTableProps {
  data: InitiativeQuestionManagementResponse['utrs'];
  utrTagMap: InitiativeQuestionManagementResponse['utrTagMap'];
  rootInitiativeUtrMap: Map<string, InitiativeUniversalTracker>;
  toggleSelectAll: () => void;
  selectedQuestions: BulkActionUtr[];
  toggleSelectQuestion: (utr: BulkActionUtr, status: CheckboxState) => void;
  isSelectAll: boolean;
  isRootOrganization: boolean;
  pageSize?: number;
  hiddenColumns?: QuestionTableColumn[];
  hiddenOptions?: ConfigurationType[];
  configureSelectedQuestion: (params: { utr: BulkActionUtr; type: ConfigurationType }) => void;
  initiativeId: string;
}

export const QuestionTable = (props: QuestionTableProps) => {
  const {
    data,
    utrTagMap,
    rootInitiativeUtrMap,
    selectedQuestions,
    toggleSelectAll,
    toggleSelectQuestion,
    isSelectAll = false,
    isRootOrganization,
    pageSize,
    hiddenColumns = [],
    hiddenOptions = [],
    configureSelectedQuestion,
    initiativeId,
  } = props;

  const { setInputOverrideType, setMetricOverrideCode } = useContext(QuestionManagementContext);
  const isQuestionSelected = (utrId: string) => selectedQuestions.some((utr) => utr._id === utrId);

  const columns: ColumnDef<UniversalTrackerBlueprintMin>[] = [
    {
      header: QUESTION.CAPITALIZED_SINGULAR,
      accessorKey: 'name',
    },
  ];

  if (!hiddenColumns.includes(QuestionTableColumn.Tags)) {
    columns.push({
      id: 'tags',
      header: 'Tags',
      cell: (c) => {
        const tags = utrTagMap[c.row.original._id];
        return tags && tags.length
          ? tags.map((tag: string) => {
              return <TagItem key={tag} groupName={tag} />;
            })
          : '-';
      },
      enableSorting: false,
    });
  }

  if (!hiddenColumns.includes(QuestionTableColumn.Decimals)) {
    columns.push({
      id: 'decimals',
      header: 'Decimals',
      meta: {
        cellProps: { style: { width: 20 } },
      },
      cell: (c) => {
        return getDisplayedInputOverrides(c.row.original, rootInitiativeUtrMap).decimal;
      },
    });
  }

  if (!hiddenColumns.includes(QuestionTableColumn.NumberScale)) {
    columns.push({
      id: 'numScale',
      header: 'NumScale',
      cell: (c) => {
        return getDisplayedInputOverrides(c.row.original, rootInitiativeUtrMap).numberScale;
      },
    });
  }

  if (!hiddenColumns.includes(QuestionTableColumn.Unit)) {
    columns.push({
      id: 'unit',
      header: 'Unit',
      cell: (c) => {
        return getDisplayedInputOverrides(c.row.original, rootInitiativeUtrMap).unit;
      },
    });
  }

  if (!hiddenColumns.includes(QuestionTableColumn.Overrides)) {
    columns.push({
      id: 'overrides',
      header: 'Overrides',
      cell: (c) => {
        return getDisplayedOverrides(c.row.original, rootInitiativeUtrMap);
      },
    });
  }

  if (!hiddenColumns.includes(QuestionTableColumn.MultiSelectAction)) {
    columns.push({
      id: 'multi-select-action',
      meta: {
        cellProps: { style: { width: 20 }, className: 'text-center' },
      },
      header: () => (
        <Button color='link' onClick={toggleSelectAll} disabled={!isRootOrganization}>
          {isSelectAll ? 'Unselect all' : 'Select all'}
        </Button>
      ),
      cell: (c) => {
        const question = c.row.original;
        const status = isQuestionSelected(question._id) ? CheckboxState.Checked : CheckboxState.Unchecked;
        return (
          <Checkbox
            status={status}
            disabled={!isRootOrganization}
            onChange={() => toggleSelectQuestion(c.row.original, status)}
          />
        );
      },
      enableSorting: false,
    });
  }

  if (!hiddenColumns.includes(QuestionTableColumn.SingleAction)) {
    columns.push({
      id: 'single-action',
      meta: {
        cellProps: { style: { width: 20 }, className: 'text-center' },
      },
      header: '',
      cell: (c) => {
        const utr = c.row.original;
        return (
          <div className='d-flex align-items-center gap-2'>
            <QuestionConfigurationDropdown
              selectedQuestions={[utr]}
              hiddenOptions={hiddenOptions}
              disabled={!isRootOrganization}
              changeConfigType={(configType) => {
                configureSelectedQuestion({ utr, type: configType });
                setInputOverrideType(undefined);
                setMetricOverrideCode(undefined);
              }}
              initiativeId={initiativeId}
            />
          </div>
        );
      },
      enableSorting: false,
    });
  }

  return <Table data={data} columns={columns} pageSize={pageSize ?? data.length} responsive />;
};
